import type { ChangeEvent, FormEvent } from "react";
import { Link } from "react-router";
import type { Listing } from "../../types/domain";
import { useCheckoutForm, type CheckoutReceipt } from "../../hooks/useCheckoutForm";
import type { CheckoutAction } from "../../schemas/checkoutFormSchemas";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { classNames } from "../ui/classNames";

interface CheckoutFormProps {
  listing: Listing;
  buyerId?: string;
}

const actionOptions: Array<{ value: CheckoutAction; label: string }> = [
  { value: "buy", label: "Buy now — pay full amount" },
  { value: "hold", label: "Request hold" },
  { value: "bid", label: "Place bid" },
];

function formValue(form: HTMLFormElement, name: string) {
  return String(new FormData(form).get(name) ?? "");
}

function ReceiptPanel({ receipt }: { receipt: CheckoutReceipt }) {
  return (
    <div className="rounded-2xl border border-emerald-300/30 bg-emerald-400/10 px-4 py-3 text-sm font-bold text-emerald-100">
      <p>{receipt.message}</p>
      <p className="mt-1 text-emerald-100/70">Receipt: {receipt.id}</p>
      {receipt.action === "buy" ? <Link className="mt-3 inline-block underline" to={receipt.orderUrl}>Open order</Link> : null}
    </div>
  );
}

export function CheckoutForm({ listing, buyerId = "pharm-green-valley" }: CheckoutFormProps) {
  const checkout = useCheckoutForm("buy");
  const result = checkout.result;
  const errors = result && !result.ok ? result.fieldErrors : {};
  const defaultQuantity = String(Math.min(2, listing.quantity));
  const defaultBidUnitPrice = String(Math.max(1, Math.round(listing.unitPrice * 0.92)));

  function onActionChange(event: ChangeEvent<HTMLSelectElement>) {
    checkout.setAction(event.target.value as CheckoutAction);
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const base = {
      action: checkout.action,
      listingId: listing.id,
      buyerId,
      quantity: formValue(form, "quantity"),
      shipTo: formValue(form, "shipTo"),
    };

    if (checkout.action === "buy") {
      checkout.submit({ ...base, action: "buy", paymentMethodId: formValue(form, "paymentMethodId") });
      return;
    }

    if (checkout.action === "bid") {
      checkout.submit({ ...base, action: "bid", unitPrice: formValue(form, "unitPrice"), expiresAt: formValue(form, "expiresAt") });
      return;
    }

    checkout.submit({ ...base, action: "hold", holdHours: formValue(form, "holdHours") });
  }

  return (
    <>
      <div className="mt-8 grid gap-4 lg:grid-cols-[1fr_360px]">
        <form className="space-y-4 rounded-2xl bg-slate-50 p-5" onSubmit={onSubmit} noValidate>
          <label className="block" htmlFor="checkout-action">
            <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">Action</span>
            <select
              className={classNames("w-full rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-slate-950 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-300")}
              id="checkout-action"
              name="action"
              onChange={onActionChange}
              value={checkout.action}
            >
              {actionOptions.map(option => <option className="bg-slate-950 text-slate-950" key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>

          <Input label="Quantity" name="quantity" type="number" min={1} max={listing.quantity} defaultValue={defaultQuantity} error={errors.quantity} />
          <Input label="Ship to" name="shipTo" defaultValue="Green Valley Rx · Seattle, WA" error={errors.shipTo} />

          {checkout.action === "buy" ? (
            <Input label="Payment" name="paymentMethodId" defaultValue="ACH ending 4822" error={errors.paymentMethodId} />
          ) : null}

          {checkout.action === "hold" ? (
            <Input label="Hold duration" name="holdHours" type="number" min={1} max={72} defaultValue="24" error={errors.holdHours} />
          ) : null}

          {checkout.action === "bid" ? (
            <>
              <Input label="Bid unit price" name="unitPrice" type="number" min={1} defaultValue={defaultBidUnitPrice} error={errors.unitPrice} />
              <Input label="Bid expiration" name="expiresAt" type="date" defaultValue="2026-06-10" error={errors.expiresAt} />
            </>
          ) : null}

          {result && !result.ok && result.formError ? <p className="rounded-2xl border border-rose-300/30 bg-rose-400/10 px-4 py-3 text-sm font-bold text-rose-100">{result.formError}</p> : null}
          {result && result.ok ? <ReceiptPanel receipt={result.receipt} /> : null}

          <div className="flex flex-wrap gap-3">
            <Button type="submit">Submit order</Button>
            <Button href={`/marketplace/listings/${listing.id}`} variant="secondary">Back to listing</Button>
          </div>
        </form>

        <aside className="rounded-2xl bg-slate-50 p-5">
          <h2 className="text-xl font-black">Order summary</h2>
          <p className="mt-3 text-slate-600">{listing.quantity} units available</p>
          <p className="mt-2 text-sm font-semibold text-slate-500">{listing.ndc} · lot {listing.lot} · packet {listing.packetStatus.replace("_", " ")}</p>
          <strong className="mt-5 block text-4xl">${(listing.unitPrice * Math.min(2, listing.quantity)).toLocaleString()}</strong>
        </aside>
      </div>
    </>
  );
}
